import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LivePayoutFeed from '../components/LivePayoutFeed';
import {
  Bitcoin,
  Link2,
  Droplets,
  TrendingUp,
  Users,
  Zap,
  Shield,
  ArrowRight,
  Copy,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [publicStats, setPublicStats] = useState({
    total_users: 0,
    total_links: 0,
    total_clicks: 0,
    total_paid_satoshis: 0,
  });
  const [demoUrl, setDemoUrl] = useState('');
  const [demoShortUrl, setDemoShortUrl] = useState('');

  const fetchPublicStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/stats`);
      setPublicStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch public stats:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchPublicStats();
  }, [fetchPublicStats]);

  const handleDemoShorten = () => {
    if (demoUrl) {
      const randomCode = Math.random().toString(36).substring(2, 8);
      setDemoShortUrl(`satoshi.sh/${randomCode}`);
      toast.success('Link shortened! Sign up to start earning.');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Could not copy - please copy manually');
    }
  };

  const features = [
    {
      icon: Link2,
      title: 'Link Shortening',
      description: 'Create short links that earn you satoshis with every click. Share anywhere and watch your earnings grow.',
      color: 'text-[#F7931A]',
      bg: 'bg-[#F7931A]/10',
    },
    {
      icon: Droplets,
      title: 'Free Faucet',
      description: 'Claim free satoshis every 15 minutes. No limits on how many times you can claim daily.',
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10',
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Stats',
      description: 'Track your earnings, clicks, and performance with beautiful real-time analytics dashboard.',
      color: 'text-cyan-400',
      bg: 'bg-cyan-400/10',
    },
    {
      icon: Shield,
      title: 'Secure Payouts',
      description: 'Direct payouts to your Bitcoin wallet. We never hold your funds longer than necessary.',
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
    },
  ];

  const howItWorks = [
    { step: '01', title: 'Sign Up Free', desc: 'Create your account in seconds' },
    { step: '02', title: 'Shorten Links', desc: 'Paste any URL to create earning links' },
    { step: '03', title: 'Share & Earn', desc: 'Share links and earn per click' },
    { step: '04', title: 'Withdraw BTC', desc: 'Cash out to your wallet anytime' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-[#F7931A]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7931A]/10 border border-[#F7931A]/30 mb-8 animate-fade-in-up">
              <Zap className="w-4 h-4 text-[#F7931A]" />
              <span className="text-sm font-medium text-[#F7931A]">
                Earn unlimited Bitcoin from your traffic
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              Turn Your Links Into{' '}
              <span className="text-gradient-gold">Bitcoin</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200">
              Shorten URLs, share them everywhere, and earn satoshis for every click. 
              Plus, claim free Bitcoin from our faucet every 15 minutes.
            </p>

            {/* Demo URL Shortener */}
            <div className="max-w-xl mx-auto mb-12 animate-fade-in-up animation-delay-300">
              <div className="flex gap-2 p-2 rounded-xl bg-[#131622] border border-white/10">
                <Input
                  type="url"
                  placeholder="Paste your long URL here..."
                  value={demoUrl}
                  onChange={(e) => setDemoUrl(e.target.value)}
                  className="flex-1 bg-transparent border-0 text-white placeholder:text-slate-500 focus-visible:ring-0"
                  data-testid="url-shortener-input"
                />
                <Button
                  onClick={handleDemoShorten}
                  className="btn-gold px-6"
                  data-testid="url-shortener-submit-btn"
                >
                  Shorten
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {demoShortUrl && (
                <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-emerald-400" />
                    <span className="font-mono text-emerald-400" data-testid="shortened-link-output">
                      {demoShortUrl}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(demoShortUrl)}
                    className="text-emerald-400 hover:text-emerald-300"
                    data-testid="copy-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/auth?tab=register">
                <Button className="btn-gold px-8 py-6 text-lg rounded-xl glow-gold">
                  Start Earning Free
                  <Bitcoin className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="px-8 py-6 text-lg rounded-xl border-white/20 hover:bg-white/5">
                  Login to Dashboard
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { label: 'Total Users', value: publicStats.total_users.toLocaleString(), icon: Users },
              { label: 'Links Created', value: publicStats.total_links.toLocaleString(), icon: Link2 },
              { label: 'Total Clicks', value: publicStats.total_clicks.toLocaleString(), icon: TrendingUp },
              { label: 'BTC Paid Out', value: `${(publicStats.total_paid_satoshis / 100000000).toFixed(2)}`, icon: Bitcoin },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className="card-dark p-4 sm:p-6 text-center animate-fade-in-up"
                style={{ animationDelay: `${0.5 + i * 0.1}s` }}
              >
                <stat.icon className="w-6 h-6 text-[#F7931A] mx-auto mb-2" />
                <div className="font-mono text-2xl sm:text-3xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Live Payout Feed */}
          <div className="mt-16">
            <LivePayoutFeed />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0D0F17]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Multiple Ways to <span className="text-gradient-gold">Earn</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Maximize your Bitcoin earnings with our comprehensive suite of tools
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className="card-dark p-6 group hover:scale-105 transition-transform duration-300"
              >
                <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              How It <span className="text-gradient-gold">Works</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Start earning Bitcoin in just a few simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((item, i) => (
              <div key={item.step} className="relative">
                <div className="card-dark p-6 text-center h-full">
                  <div className="font-mono text-4xl font-bold text-[#F7931A]/30 mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
                {i < 3 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-3 w-6 h-6 text-[#F7931A]/50 transform -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0D0F17]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="card-dark p-8 sm:p-12 glow-gold-lg relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F7931A]/10 to-transparent" />
            <div className="relative">
              <Bitcoin className="w-16 h-16 text-[#F7931A] mx-auto mb-6" />
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                Start Earning Bitcoin Today
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto mb-8">
                Join thousands of users already earning free Bitcoin through link sharing and our generous faucet.
              </p>
              <Link to="/auth?tab=register">
                <Button className="btn-gold px-10 py-6 text-lg rounded-xl">
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Bitcoin className="w-6 h-6 text-[#F7931A]" />
            <span className="font-heading font-bold text-gradient-gold">SatoshiShort</span>
          </div>
          <p className="text-sm text-slate-500">
            © 2024 SatoshiShort. Earn Bitcoin with every click.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
